import { useState } from "react";
import client, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Coffee, Building2, GraduationCap, Wrench, ArrowRight } from "lucide-react";

const businessTypes = [
  "Café / Coffee Shop",
  "Hotel / Resort",
  "Restaurant / QSR",
  "Corporate Office",
  "Co-working Space",
  "Institution / University",
  "Cloud Kitchen",
  "Retail / Distributor",
  "Other",
];

const interests = [
  { id: "beans", label: "Beans & Blends" },
  { id: "equipment", label: "Equipment (Machines / Grinders)" },
  { id: "private-label", label: "Private Label Manufacturing" },
  { id: "training", label: "Barista Training" },
  { id: "cafe-setup", label: "Café Setup & Consulting" },
  { id: "corporate", label: "Corporate Coffee Program" },
];

const pillars = [
  { icon: Coffee, title: "Beans & Blends", desc: "Fresh-roasted, custom-blended for your menu." },
  { icon: Wrench, title: "Equipment", desc: "Commercial espresso machines & grinders — sale or lease." },
  { icon: GraduationCap, title: "Training", desc: "SCA-informed barista programs delivered on-site." },
  { icon: Building2, title: "Café Setup", desc: "Turnkey café design, menu, and profitability consulting." },
];

export default function Business() {
  const [form, setForm] = useState({
    company: "",
    contact_name: "",
    email: "",
    phone: "",
    business_type: businessTypes[0],
    city: "",
    monthly_volume: "",
    interests: [],
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleInterest = (id) =>
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(id)
        ? f.interests.filter((x) => x !== id)
        : [...f.interests, id],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await client.post("/business-enquiries", form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Please try again");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="pt-40 pb-24 max-w-3xl mx-auto px-6 text-center" data-testid="business-thanks">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32] mb-4">Thank you</div>
        <h1 className="font-serif-display text-5xl md:text-6xl font-light leading-none">
          We'll be in touch<br />within 24 hours.
        </h1>
        <p className="mt-6 text-[#4A3B32]">
          Our B2B team will reach out to <b>{form.email}</b> with a tailored proposal.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="business-page" className="pt-32 pb-24">
      {/* Hero */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 mb-16">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32] mb-4">
          For Business
        </div>
        <h1 className="font-serif-display text-5xl md:text-7xl font-light leading-[0.95] max-w-4xl">
          Build a coffee program<br />that <em className="not-italic text-[#C85A32]">pays back</em>.
        </h1>
        <p className="mt-8 max-w-3xl text-lg text-[#4A3B32] leading-relaxed">
          From ten-cup offices to nationwide café chains — we deliver end-to-end coffee solutions
          for cafés, hotels, restaurants, corporates, and institutions across India.
        </p>
      </section>

      {/* Pillars */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 mb-24">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#EBE3D5] border border-[#EBE3D5]">
          {pillars.map((p) => (
            <div key={p.title} className="bg-[#FDFBF7] p-8">
              <p.icon size={22} strokeWidth={1.4} className="text-[#C85A32] mb-6" />
              <div className="font-serif-display text-xl text-[#1C3326] leading-tight mb-2">
                {p.title}
              </div>
              <p className="text-sm text-[#4A3B32]">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Form */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32] mb-4">
            Tell us about your business
          </div>
          <h2 className="font-serif-display text-4xl md:text-5xl font-light leading-tight">
            Fill this. We'll craft a proposal in 24 hours.
          </h2>
          <div className="mt-10 space-y-5 text-[#4A3B32]">
            <div className="flex items-start gap-3">
              <ArrowRight size={16} className="text-[#C85A32] mt-1 shrink-0" />
              <div>
                <div className="font-medium text-[#1C3326]">Sample kits</div>
                <div className="text-sm">We ship a curated sampler to your address for cupping.</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <ArrowRight size={16} className="text-[#C85A32] mt-1 shrink-0" />
              <div>
                <div className="font-medium text-[#1C3326]">Volume pricing</div>
                <div className="text-sm">Custom pricing tiers based on your monthly consumption.</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <ArrowRight size={16} className="text-[#C85A32] mt-1 shrink-0" />
              <div>
                <div className="font-medium text-[#1C3326]">Dedicated account manager</div>
                <div className="text-sm">One point of contact for beans, machines, and training.</div>
              </div>
            </div>
          </div>
        </div>

        <form
          onSubmit={submit}
          className="lg:col-span-7 border border-[#EBE3D5] p-6 md:p-10 space-y-5 bg-[#FDFBF7]"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Company / Brand" required>
              <input required value={form.company} onChange={set("company")} data-testid="b2b-company" className={inputCls} />
            </Field>
            <Field label="Your name" required>
              <input required value={form.contact_name} onChange={set("contact_name")} data-testid="b2b-name" className={inputCls} />
            </Field>
            <Field label="Email" required>
              <input required type="email" value={form.email} onChange={set("email")} data-testid="b2b-email" className={inputCls} />
            </Field>
            <Field label="Phone" required>
              <input required value={form.phone} onChange={set("phone")} data-testid="b2b-phone" className={inputCls} />
            </Field>
            <Field label="Business type">
              <select value={form.business_type} onChange={set("business_type")} data-testid="b2b-type" className={inputCls}>
                {businessTypes.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </Field>
            <Field label="City">
              <input value={form.city} onChange={set("city")} data-testid="b2b-city" className={inputCls} />
            </Field>
          </div>

          <Field label="Estimated monthly volume (kg or cups)">
            <input value={form.monthly_volume} onChange={set("monthly_volume")} placeholder="e.g. 30 kg / 5,000 cups" data-testid="b2b-volume" className={inputCls} />
          </Field>

          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-[#7B6F66] mb-3">
              What are you interested in?
            </div>
            <div className="flex flex-wrap gap-2">
              {interests.map((i) => (
                <button
                  key={i.id}
                  type="button"
                  onClick={() => toggleInterest(i.id)}
                  data-testid={`b2b-interest-${i.id}`}
                  className={`border px-4 py-2 text-xs uppercase tracking-[0.15em] transition-colors ${
                    form.interests.includes(i.id)
                      ? "border-[#1C3326] bg-[#1C3326] text-[#FDFBF7]"
                      : "border-[#EBE3D5] hover:border-[#1C3326]"
                  }`}
                >
                  {i.label}
                </button>
              ))}
            </div>
          </div>

          <Field label="Tell us more (optional)">
            <textarea rows={4} value={form.message} onChange={set("message")} data-testid="b2b-message" className={inputCls} />
          </Field>

          <button
            type="submit"
            disabled={busy}
            data-testid="b2b-submit"
            className="w-full bg-[#C85A32] hover:bg-[#A84A28] text-[#FDFBF7] py-4 uppercase text-xs tracking-[0.3em] font-medium transition-colors disabled:opacity-60"
          >
            {busy ? "Sending…" : "Send Enquiry"}
          </button>
        </form>
      </section>
    </div>
  );
}

const inputCls =
  "w-full bg-transparent border border-[#EBE3D5] px-4 py-3 focus:border-[#1C3326] focus:outline-none";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-[0.15em] text-[#7B6F66] mb-2">
        {label} {required && <span className="text-[#C85A32]">*</span>}
      </div>
      {children}
    </label>
  );
}
